import loggingService from 'common/logging/loggingService';
import notificationService from 'common/notificationService';
import AsyncButton from 'common/ui/AsyncButton';
import ValidationError from 'common/validationError';
import { Partner } from 'features/settings/importMapping/partner';
import React, { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import partnerIntegrationService, { ManualImportRequest, ManualImportType } from '../partnerIntegrationService';
import ImportForCustomersDetails from './ImportForCustomerDetails';
import { ImportByDateDetails, ImportSpecificRecordsDetails } from './ImportFromCosmosDetails';
import CustomImportDetails from './customImportDetails/CustomImportDetails';

export interface ErpImportModalProps {
    merchantId: string;
    partner: Partner;
    onHide(): void;
}

function getBlankRequest(): ManualImportRequest {
    return {
        type: null,
        recordIds: [],
        dataTypes: [],
        importBeforeDate: new Date(),
        importAfterDate: null
    }
}

const ErpImportModal: React.FC<ErpImportModalProps> = props => {

    const [request, setRequest] = useState<ManualImportRequest>(getBlankRequest());
    const [areDetailsValid, setAreDetailsValid] = useState(true);

    const customerImport = { name: "Customers", type: ManualImportType.ForCustomers };

    let importTypes = [
        { name: "Retry records by date", type: ManualImportType.ByDate },
        { name: "Specific record(s)", type: ManualImportType.SpecificRecords }
    ];

    switch (props.partner) {
        case Partner.Agris:
        case Partner.AgVision:
        case Partner.Agvance:
            importTypes = [customerImport, { name: "Custom Record(s)", type: ManualImportType.Custom }, ...importTypes];
            break;
        case Partner.Oakland:
        case Partner.EverAg:
            importTypes = [customerImport, ...importTypes];
            break;
        case Partner.AgVantage:
        case Partner.Intellego:
            importTypes = [{ name: "Contracts", type: ManualImportType.Custom }, ...importTypes];
            break;
    }

    function isValid(): boolean {
        if (request.type == null) return false;

        return areDetailsValid;
    }

    function setImportType(requestType: ManualImportType) {
        setRequest({...getBlankRequest(), type: requestType})
    }

    async function onRun() {
        try {
            await partnerIntegrationService.performManualErpImport(props.merchantId, request);
            notificationService.success("Manual import started");
        }
        catch (err) {
            loggingService.error("Unable to perform manual import", err as Error);
            const validationError = err as ValidationError;
            notificationService.error(validationError.errors?.join(", ") ?? "Unable to perform manual import");
        }
        setRequest(getBlankRequest());
        props.onHide();
    }

    return <Modal show onHide={props.onHide} backdrop="static" size="lg">
        <Modal.Header closeButton>
            <Modal.Title>
                Import...
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>

            <div>
                {importTypes.map((it, i) => <Form.Check
                    key={i}
                    type="radio"
                    inline
                    label={it.name}
                    name="importType"
                    id={"importType" + i}
                    checked={request.type === it.type}
                    onChange={() => setImportType(it.type)}
                />)}
            </div>

            <div className="mt-4">
                {request.type === ManualImportType.ForCustomers &&
                    <ImportForCustomersDetails 
                        partner={props.partner}
                        request={request} 
                        onChange={setRequest} 
                        onIsValidChanged={setAreDetailsValid}
                    />
                }

                {request.type === ManualImportType.ByDate &&
                    <ImportByDateDetails 
                        partner={props.partner}
                        request={request} 
                        onChange={setRequest} 
                        onIsValidChanged={setAreDetailsValid}
                    />
                }
                
                {request.type === ManualImportType.SpecificRecords &&
                    <ImportSpecificRecordsDetails 
                        partner={props.partner}
                        request={request} 
                        onChange={setRequest} 
                        onIsValidChanged={setAreDetailsValid}
                    />
                }
                
                {request.type === ManualImportType.Custom &&
                    <CustomImportDetails 
                        partner={props.partner}
                        request={request} 
                        onChange={setRequest} 
                        onIsValidChanged={setAreDetailsValid}
                    />
                }
            </div>

        </Modal.Body>
        <Modal.Footer>
            <div className="pull-right">
                <Button variant="secondary" className="ml-2" onClick={props.onHide}>
                    Cancel
                </Button>&nbsp;
                <AsyncButton variant="primary" onClickAsync={onRun} disabled={!isValid()}>
                    Run
                </AsyncButton>
            </div>
        </Modal.Footer>
    </Modal>;

}









export default ErpImportModal;